import { useContext } from 'react';
import {
    DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown,
} from 'reactstrap';
import { APIProvider } from '@vis.gl/react-google-maps';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from '@s20/hooks/redux.hooks';
import { getTenantSettings } from '@s20/selectors/features/common.selectors';
import { Hide } from '@s20/components/common/Hide';
import { FormContext, FormValidated } from '../../common/forms/FormElements';
import Input from '../../common/forms/Input';
import ValidatedSelect, { CompactSelectStyles } from '../../common/forms/ValidatedSelect';
import { MapView } from './MapView';
import { MAX_RANGE_DAYS } from './MapView/useJobQuery';

const addMaxRange = (dateFrom) => moment(dateFrom, 'YYYY-MM-DD')
    .add(MAX_RANGE_DAYS - 1, 'days')
    .format('YYYY-MM-DD');

// Start/end date pair for the map filters. The end date is capped at MAX_RANGE_DAYS
// (inclusive) from the start date, which is also the cap the query enforces.
const DateRangeInputs = ({ size, formGroupClassName = '' }) => {
    const { values, setFieldValue } = useContext(FormContext);

    // A new range means a new set of jobs, so drop the open map pane rather than leave it
    // showing jobs from the range that was on screen when it was opened.
    const closeInfoPane = () => {
        setFieldValue('selectedJob', null);
        setFieldValue('jobGroup', null);
    };

    const onDateFromChange = (e) => {
        closeInfoPane();

        const dateFrom = e.target.value;
        if (!dateFrom) return;

        const maxDateTo = addMaxRange(dateFrom);
        if (values.dateTo < dateFrom) setFieldValue('dateTo', dateFrom);
        else if (values.dateTo > maxDateTo) setFieldValue('dateTo', maxDateTo);
    };

    return (
        <>
            <Input
                type="date"
                name="dateFrom"
                size={size}
                formGroupClassName={formGroupClassName}
                onChange={onDateFromChange}
            />

            <Input
                type="date"
                name="dateTo"
                size={size}
                min={values.dateFrom}
                max={addMaxRange(values.dateFrom)}
                onChange={closeInfoPane}
            />
        </>
    );
};

export const JobMapView = ({ dispatchLocations }) => {
    const { googleMapsApiKey, mapId } = useSelector(getTenantSettings);

    const mFrom = moment().startOf('day');
    const mTo = mFrom.clone();

    return (
        <FormValidated
            id="dailyJobViewMap"
            className="d-flex flex-column w-100 flex-fill"
            initialValues={{
                dispatchOfficeLocationId: null,
                dateFrom: mFrom.format('YYYY-MM-DD'),
                dateTo: mTo.format('YYYY-MM-DD'),
                jobNumber: '',
            }}
        >
            {() => (
                <>
                    <header className="flex-none d-flex align-items-center justify-content-end">
                        {/* Desktop Filters - Inline */}
                        <Hide mdUp>
                            <div className="d-flex mr-2">
                                <DateRangeInputs formGroupClassName="mb-0 mr-2" />
                            </div>

                            <ValidatedSelect
                                formGroupClassName="mb-0 mr-2"
                                styles={{
                                    ...CompactSelectStyles,
                                    container: (base) => ({
                                        ...base,
                                        minWidth: 200,
                                        flexShrink: 0,
                                    }),
                                }}
                                name="dispatchOfficeLocationId"
                                placeholder="Select Dispatch Office"
                                options={dispatchLocations}
                                getOptionLabel={(option) => option.label}
                                getOptionValue={(option) => option.value}
                                isClearable
                            />

                            <Input
                                type="search"
                                placeholder="Search jobs..."
                                formGroupClassName="mb-0"
                                name="jobNumber"
                            />
                        </Hide>

                        {/* Mobile/Tablet Filters - Dropdown */}
                        <Hide smDown>
                            <UncontrolledDropdown>
                                <DropdownToggle
                                    color="outline-secondary"
                                    size="sm"
                                >
                                    <FontAwesomeIcon
                                        icon={faFilter}
                                        className="mr-1"
                                    />
                                    Filters
                                </DropdownToggle>

                                <DropdownMenu>
                                    <DropdownItem header>Date</DropdownItem>
                                    <div className="px-3 py-1">
                                        <DateRangeInputs size="sm" />
                                    </div>

                                    <DropdownItem divider />

                                    <DropdownItem header>Search</DropdownItem>
                                    <div className="px-3 py-1">
                                        <Input
                                            type="search"
                                            size="sm"
                                            name="jobNumber"
                                        />
                                    </div>

                                    <DropdownItem divider />

                                    <DropdownItem header>Office</DropdownItem>
                                    <div className="px-3 py-1">
                                        <ValidatedSelect
                                            formGroupClassName="mb-0 mr-2"
                                            styles={CompactSelectStyles}
                                            name="dispatchOfficeLocationId"
                                            placeholder="Select Dispatch Office"
                                            options={dispatchLocations}
                                            getOptionLabel={(option) => option.label}
                                            getOptionValue={(option) => option.value}
                                            isClearable
                                        />
                                    </div>
                                </DropdownMenu>
                            </UncontrolledDropdown>
                        </Hide>
                    </header>


                    <APIProvider
                        apiKey={googleMapsApiKey}
                        libraries={['marker']}
                        version="quarterly"
                    >
                        <MapView
                            mapId={mapId}
                            dispatchLocations={dispatchLocations}
                        />
                    </APIProvider>
                </>
            )}
        </FormValidated>
    );
};